import * as React from 'react';
import * as moment from "moment";
// @ts-ignore
import { Chart } from "chart.js";
import { isEmpty as _isEmpty, map as _map } from 'lodash';


// draws a rectangle with a rounded top
Chart.helpers.drawRoundedTopRectangle = function (ctx: any, x: number, y: number, width: number, height: number, radius: number) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  // top right corner
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  // bottom right	corner
  ctx.lineTo(x + width, y + height);
  // bottom left corner
  ctx.lineTo(x, y + height);
  // top left	
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

Chart.controllers.bar = Chart.controllers.bar.extend({
  dataElementType: Chart.elements.Rectangle.extend({
    draw: function () {

      const ctx = this._chart.ctx;
      const vm = this._view;
      let left, right, top, bottom, signX, signY, borderSkipped;
      let borderWidth = vm.borderWidth;

      if (!vm.horizontal) {
        // bar
        left = vm.x - vm.width / 2;
        right = vm.x + vm.width / 2;
        top = vm.y;
        bottom = vm.base;
        signX = 1;
        signY = bottom > top ? 1 : -1;
        borderSkipped = vm.borderSkipped || 'bottom';
      } else {
        // horizontal bar
        left = vm.base;
        right = vm.x;
        top = vm.y - vm.height / 2;
        bottom = vm.y + vm.height / 2;
        signX = right > left ? 1 : -1;
        signY = 1;
        borderSkipped = vm.borderSkipped || 'left';
      }

      // Canvas doesn't allow us to stroke inside the width so we can
      // adjust the sizes to fit if we're setting a stroke on the line
      if (borderWidth) {
        // borderWidth shold be less than bar width and bar height.
        const barSize = Math.min(Math.abs(left - right), Math.abs(top - bottom));
        borderWidth = borderWidth > barSize ? barSize : borderWidth;
        const halfStroke = borderWidth / 2;
        // Adjust borderWidth when bar top position is near vm.base(zero).
        const borderLeft = left + (borderSkipped !== 'left' ? halfStroke * signX : 0);
        const borderRight = right + (borderSkipped !== 'right' ? -halfStroke * signX : 0);
        const borderTop = top + (borderSkipped !== 'top' ? halfStroke * signY : 0);
        const borderBottom = bottom + (borderSkipped !== 'bottom' ? -halfStroke * signY : 0);
        // not become a vertical line?
        if (borderLeft !== borderRight) {
          top = borderTop;
          bottom = borderBottom;
        }
        // not become a horizontal line?
        if (borderTop !== borderBottom) {
          left = borderLeft;
          right = borderRight;
        }
      }

      // calculate the bar width and roundess
      const barWidth = Math.abs(left - right);
      const roundness = this._chart.config.options.barRoundness || 0.5;
      const radius = barWidth * roundness * 0.5;

      // keep track of the original top of the bar
      const prevTop = top;

      // move the top down so there is room to draw the rounded top
      top = prevTop + radius;
      const barRadius = top - prevTop;

      ctx.beginPath();
      ctx.fillStyle = vm.backgroundColor;
      ctx.strokeStyle = vm.borderColor;
      ctx.lineWidth = borderWidth;

      // draw the rounded top rectangle
      Chart.helpers.drawRoundedTopRectangle(ctx, left, (top - barRadius + 1), barWidth, bottom - prevTop, barRadius);

      ctx.fill();
      if (borderWidth) {
        ctx.stroke();
      }

      // restore the original top value so tooltips and scales still work
      top = prevTop;
    },
  })
});

Chart.defaults.global.defaultFontFamily = "BrandonTextWeb-Regular";


const chartColors = {
  red: 'rgb(255, 99, 132)',
  orange: 'rgb(255, 159, 64)',
  yellow: 'rgb(255, 205, 86)',
  green: 'rgb(75, 192, 192)',
  blue: 'rgb(54, 162, 235)',
  purple: 'rgb(153, 102, 255)',
  grey: 'rgb(231,233,237)'
};


export interface IBarChartData {
  x: string,
  y: number,
  barColor?: string
}

interface IBarChartProps {
  header?: string,
  readings: IBarChartData[],
  threshold?: {
    low: number,
    high: number,
  }
}

interface IBarData {
  x: number,
  width: number,
}

const BarChart = (props: IBarChartProps) => {
  const chartRef: any = React.createRef();
  const [lowestValueBars, setLowestValueBars] = React.useState<IBarData[]>([] as IBarData[]);
  const [highestValueBars, setHighestValueBars] = React.useState<IBarData[]>([] as IBarData[]);


  React.useEffect(() => {
    const ctx = chartRef.current.getContext("2d");


    new Chart(ctx, {
      type: "bar",
      data: {
        labels: props.readings.map(reading => reading.x),
        datasets: [{
          barPercentage: 0.4,
          //barThickness: 6,
          //maxBarThickness: 8,
          //minBarLength: 2,
          backgroundColor: props.readings.map(reading => reading.barColor),
          data: props.readings.map(reading => reading.y),
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        barRoundness: 0.5,
        layout: {
          padding: {
            top: 15,
          }
        },
        title: {
          display: false,
        },
        legend: {
          display: false,
        },
        scales: {
          yAxes: [{
            display: true,
            gridLines: {
              drawBorder: false,
            },
            ticks: {
              max: 100,
              display: true,
              maxTicksLimit: 6,
              fontSize: 9,
              beginAtZero: true,
            }
          }],
          xAxes: [{
            gridLines: {
              display: false,
            },
            ticks: {
              beginAtZero: true,
              fontSize: 9,
            },
          }]
        },
        events: [], //this will disable all tooltip events
        tooltips: {
          enabled: false,
          custom: (tooltipModel: any) => { }
        },
        animation: {
          hover: {
            animationDuration: 0
          },
          duration: 1,
          onComplete: function () {
            const chartInstance = this.chart,
              ctx = chartInstance.ctx;

            ctx.font = Chart.helpers.fontString(12, 700, 'BrandonTextWeb-Bold');
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';

            const highestValueBars: IBarData[] = [] as IBarData[];
            const lowestValueBars: IBarData[] = [] as IBarData[];

            this.data.datasets.forEach(function (dataset: any, i: number) {
              const meta = chartInstance.controller.getDatasetMeta(i);
              meta.data.forEach(function (bar: any, index: number) {
                const barValue = dataset.data[index];
                if (barValue > props.threshold.high) {
                  const currBar = {
                    x: bar._model.x, width: bar._model.width
                  }
                  highestValueBars.push(currBar);
                }
                else if (barValue < props.threshold.low) {
                  const currBar = {
                    x: bar._model.x, width: bar._model.width
                  }
                  lowestValueBars.push(currBar);
                }

                ctx.fillText(barValue, bar._model.x, bar._model.y - 2);
              });
            });
            setHighestValueBars(highestValueBars);
            setLowestValueBars(lowestValueBars);
          }
        },
      },
    });
  }, []);


  return (
    <div className={"bar-chart-container"}>
      <div className={"bar-chart-header"}>
        <span className={"bar-chart-heading"}>{props.header}</span>
      </div>
      <div className="bar-chart-body">
        <div className="chart-wrapper" style={{ width: "auto", height: "220px", position: "relative" }}>
          <canvas id="myBarChart" ref={chartRef as any} style={{ width: "100%", height: "220px" }} />
        </div>
        <div className="bar-chart-footer">
          {!_isEmpty(lowestValueBars) ? _map(lowestValueBars, (bar: IBarData, i: number) => <img key={"waarning-" + i} style={{ left: `${bar.x - 7.5}px` }} src="/images/warning.svg" alt="lowest" />) : null}
          {!_isEmpty(highestValueBars) ? _map(highestValueBars, (bar: IBarData, i: number) => <img key={"trophy-" + i} style={{ left: `${bar.x - 7}px` }} src="/images/trophy.svg" alt="highest" />) : null}
        </div>
      </div>
    </div>
  );
}

export default BarChart;
